import * as path from 'path';

import voca from 'voca';
import { z } from 'zod';

import { S3Resource } from 'libs/shared-types';

import { ProviderConfiguration } from './configuration';
import { Language } from './language';
import { TimedScript } from './timing';

export const TTS_ADMIN_PATH = 'services/tts/admin';
export const TTS_OUTPUT_PATH = 'services/tts/output';

export const DEFAULT_PACE = 2.5;

export const enum PaceDescription {
  Fast = 'Fast',
  Slow = 'Slow',
  Standard = 'Standard',
}

/**
 * A left-closed interval [left, right).
 */
export class PaceInterval {
  constructor(public left: number, public right: number) {}

  contains(x: number) {
    return this.left <= x && x < this.right;
  }
}

export class PaceBand {
  constructor(public description: PaceDescription, public interval: PaceInterval) {}
}

export const PACE_BANDS: PaceBand[] = [
  new PaceBand(PaceDescription.Slow, new PaceInterval(0, 2.5)),
  new PaceBand(PaceDescription.Standard, new PaceInterval(2.5, 2.75)),
  new PaceBand(PaceDescription.Fast, new PaceInterval(2.75, Infinity)),
];

export type ProviderSpeakerID = string | number;

// Add this near the Speaker class definition
export const SpeakerSchema = z
  .object({
    id: z.string(),
    providerName: z.string(),
    idAtProvider: z.union([z.string(), z.number()]),
    name: z.string(),
    providerStyles: z.array(z.string()),
    providerModel: z.string().nullable().optional(),
    language: z.object({
      code: z.string(),
      language: z.string(),
      region: z.string(),
      paceText: z.string(),
      previewText: z.string(),
    }),
    vibes: z.array(z.string()).optional(),
    uiDescriptors: z.array(z.string()).optional(),
    accent: z.array(z.string()).optional(),
    textCharacterLimit: z.number().optional(),
    timbre: z.array(z.string()),
    pace: z.number(),
    previewURL: z.string().optional(),
    paceURL: z.string().optional(),
    isAutomationCandidate: z.boolean(),
    isEnabled: z.boolean(),
  })
  .nullable();

export class Speaker {
  /**
   * Our ID for the speaker.
   */
  public id: string;

  /**
   * The speaker's provider.
   */
  public providerName: string;

  /**
   * The provider's ID for the speaker.
   */
  public idAtProvider: ProviderSpeakerID;

  /**
   * The speaker's name.
   */
  public name: string;

  /**
   * The provider's description of the speaker's style.
   */
  public providerStyles: string[];

  /**
   * Some providers specify a model that works best for a speaker.
   */
  public providerModel?: string | null;

  public language: Language;

  /**
   * Could be fun, professional, inspiring, laid-back.
   */
  public vibes?: string[];

  /**
   * Optional UI-specific descriptors to be displayed in the client. Order matters.
   */
  public uiDescriptors?: string[];

  /**
   * Optional curated accent tag(s) for the speaker.
   */
  public accent?: string[];

  /**
   * The limit on the number of characters this speaker can synthesize to speech, if any.
   */
  public textCharacterLimit?: number;

  /**
   * Could be alto to bass, breathy, husky, rich, mellow. But it's just masculine or feminine for now.
   */
  public timbre: string[];

  /**
   * The speaker's pace in words per second.
   */
  public pace: number;

  /**
   * The URL of the speaker's preview audio.
   */
  public previewURL?: string;
  public paceURL?: string;

  public isAutomationCandidate = true;

  public isEnabled = true;

  /**
   * Return the description of the speaker's pace.
   */
  public get paceDescription() {
    for (const band of PACE_BANDS) {
      if (band.interval.contains(this.pace)) {
        return band.description;
      }
    }

    return null;
  }

  constructor(speakerData: SpeakerData) {
    const language = new Language(
      speakerData.language.code,
      speakerData.language.language,
      speakerData.language.region,
      speakerData.language.paceText,
      speakerData.language.previewText,
    );
    if (!language) {
      throw new Error('Speaker must have a supported language.');
    }

    this.id = speakerData.id;
    this.providerName = speakerData.providerName;
    this.idAtProvider = speakerData.idAtProvider;
    this.name = speakerData.name;
    this.providerStyles = speakerData.providerStyles;
    this.providerModel = speakerData.providerModel;
    this.language = language;
    this.vibes = speakerData.vibes || [];
    this.uiDescriptors = speakerData.uiDescriptors;
    this.accent = speakerData.accent || [];
    this.timbre = speakerData.timbre;
    this.pace = speakerData.pace || DEFAULT_PACE;
    this.previewURL = speakerData.previewURL;
    this.isAutomationCandidate = speakerData.isAutomationCandidate === false ? false : true;
    this.isEnabled = speakerData.isEnabled ?? true;
  }

  /**
   * Returns the speaker in a form that can be shipped around in JSON APIs.
   *
   * I hate this. Don't @ me.
   */
  public asSpeakerData(): SpeakerData {
    return {
      id: this.id,
      idAtProvider: this.idAtProvider,
      providerName: this.providerName,
      name: this.name,
      providerStyles: this.providerStyles,
      language: this.language,
      vibes: this.vibes,
      uiDescriptors: this.uiDescriptors,
      accent: this.accent,
      timbre: this.timbre,
      pace: this.pace,
      previewURL: this.previewURL,
      textCharacterLimit: this.textCharacterLimit,
      isAutomationCandidate: this.isAutomationCandidate,
      isEnabled: this.isEnabled,
    };
  }

  public estimateDuration(text: string): number {
    const estimate = voca.countWords(text) / this.pace;
    return Number.parseFloat(estimate.toFixed(2));
  }
}

export type SpeakerData = Omit<
  Speaker,
  | 'asSpeakerData'
  | 'estimateDuration'
  | 'isAutomationCandidate'
  | 'paceDescription'
  | 'provider'
  | 'pace'
  | 'isEnabled'
> & {
  isAutomationCandidate?: boolean;
  providerName: string;
  pace?: number;
  isEnabled?: boolean;
};

export type PaceRange = {
  min: number;
  max: number;
};

export class SpeakerMap extends Map<string, Speaker> {
  /**
   * Returns a list of the speakers, sorted by name.
   */
  asList(): Speaker[] {
    return Array.from(this.values()).sort((a, b) => {
      return a.language.code.localeCompare(b.language.code) || a.name.localeCompare(b.name);
    });
  }

  /**
   * Returns the map represented as a Record, so it can be easily conveyed in JSON.
   */
  asSpeakerDataRecords(): Record<string, SpeakerData> {
    return Object.fromEntries(
      this.asList().map((speaker) => [speaker.id, speaker.asSpeakerData()]),
    );
  }

  filter(filterCallback: (speaker: Speaker) => boolean): SpeakerMap {
    const filteredMap = new SpeakerMap();

    for (const [speakerID, speaker] of this.entries()) {
      if (filterCallback(speaker)) {
        filteredMap.set(speakerID, speaker);
      }
    }

    return filteredMap;
  }

  /**
   * Returns the subset of speakers that are candidates for automation.
   */
  automationCandidates(): SpeakerMap {
    return this.filter(
      (speaker) => speaker.isEnabled === true && speaker.isAutomationCandidate === true,
    );
  }

  /**
   * Returns the subset of speakers with the given language code.
   */
  filterByLanguage(code: string): SpeakerMap {
    const exactMatches = this.filter((speaker) => speaker.language.code === code);

    if (exactMatches.size > 0) {
      return exactMatches;
    }

    // if nothing matched, maybe the code is just a language, without region
    return this.filter((speaker) => speaker.language.getBaseLanguageCode() === code);
  }

  /**
   * Returns the subset of speakers matching the given pace description.
   */
  filterByPace(paceDescription: PaceDescription): SpeakerMap {
    return this.filter((speaker) => speaker.paceDescription === paceDescription);
  }

  /**
   * Returns the subset of speakers matching the given pace description.
   */
  filterByTimbre(timbre: string): SpeakerMap {
    return this.filter((speaker) => speaker.timbre.includes(timbre));
  }

  /**
   * Returns the subset of speakers matching any of the given vibes.
   */
  filterByVibe(vibe: string | string[]): SpeakerMap {
    if (Array.isArray(vibe)) {
      // If vibe is an array, return speakers that match any vibe in the array
      return this.filter((speaker) => vibe.some((v) => (speaker.vibes ?? []).includes(v)));
    }
    // Otherwise, return speakers that match the single vibe
    return this.filter((speaker) => (speaker.vibes ?? []).includes(vibe));
  }

  /**
   * Group by language.
   */
  groupByLanguage(): Record<string, SpeakerMap> {
    const speakersByLanguage: Map<string, SpeakerMap> = new Map();
    for (const speaker of this.asList()) {
      const key = speaker.language.getLabel();
      let speakers = speakersByLanguage.get(key);
      if (!speakers) {
        speakers = new SpeakerMap();
        speakersByLanguage.set(key, speakers);
      }
      speakers.set(speaker.id, speaker);
    }
    const record: Record<string, SpeakerMap> = {};
    for (const [language, speakerMap] of Array.from(speakersByLanguage.entries()).sort((a, b) =>
      a[0].localeCompare(b[0]),
    )) {
      record[language] = speakerMap;
    }
    return record;
  }

  /**
   * Merge another SpeakerMap into this one.
   */
  merge(speakerMap: SpeakerMap): SpeakerMap {
    for (const speaker of speakerMap.values()) {
      if (this.has(speaker.id)) {
        throw new Error(`Cannot add duplicate speaker ID ${speaker.id}`);
      }
      this.set(speaker.id, speaker);
    }
    return this;
  }

  multiFilter(filters: {
    timbre?: Array<string> | null;
    language?: Array<string> | null;
    pace?: Array<PaceDescription> | null;
    vibe?: Array<string> | null;
  }): SpeakerMap {
    return this.filter((speaker) => {
      if (filters.timbre && !speaker.timbre.find((timbre) => filters.timbre?.includes(timbre))) {
        // return false if the speaker didn't match the timbre filter
        return false;
      }

      if (
        filters.pace &&
        (!speaker.paceDescription || !filters.pace.includes(speaker.paceDescription))
      ) {
        // return false if the speaker didn't match the pace filter
        return false;
      }

      if (filters.language && !filters.language.includes(speaker.language.code)) {
        // return false if the speaker didn't match the language filter
        return false;
      }

      if (filters.vibe && !(speaker.vibes ?? []).find((vibe) => filters.vibe?.includes(vibe))) {
        // return false if the speaker didn't match the vibe filter
        return false;
      }

      return true;
    });
  }

  getPaceRange(): PaceRange {
    const speakersSortedByPace = this.asList()
      .map((speaker) => speaker.pace)
      .sort();
    const min = speakersSortedByPace[0];
    const max = speakersSortedByPace[speakersSortedByPace.length - 1];
    return { min, max };
  }
}

export const enum TTSACL {
  Private = 'private',
  Public = 'public-read',
}

export type TextToSpeechOptions = {
  speaker: Speaker;
  text: string;
  timeout: number;
  overwrite?: boolean;
  location?: S3Resource;
  acl?: TTSACL;
  languageCode?: string;
  videoDuration?: number;
  voiceDirectionPrompt?: string;
};

export interface TextToSpeechProvider {
  /**
   * Retrieve the provider's configuration.
   */
  getConfiguration(): Promise<ProviderConfiguration>;

  /**
   * Returns the name of this provider.
   */
  getName(): string;

  /**
   * Returns the Speaker record for the given ID.
   */
  getSpeaker(speakerID: string): Promise<Speaker | null>;

  /**
   * Returns all the provider's Speakers.
   */
  getSpeakers(): Promise<SpeakerMap>;

  /**
   * Returns the maximum length of text, in characters, that this provider can synthesize.
   */
  getTextCharacterLimit(): number;

  /**
   * Perform whatever initialization the provider requires.
   */
  initialize(): Promise<void>;

  /**
   * Creates preview audio for a speaker.
   */
  previewSpeaker(
    speaker: Speaker,
    timeout: number,
    overwrite?: boolean,
    calculatePace?: boolean,
  ): Promise<Speaker>;

  /**
   * Submits text to be voiced by the given Speaker.
   *
   * This will submit a TTS request to the provider with callProvider.
   */
  textToSpeech(options: TextToSpeechOptions): Promise<TimedScript>;
}

export class ProviderMap extends Map<string, TextToSpeechProvider> {
  addProvider(provider: TextToSpeechProvider): void {
    this.set(provider.getName(), provider);
  }
}

export function getSpeakerListLocator(): S3Resource {
  if (!process.env.ASSET_BUCKET) {
    throw new Error('No S3 bucket defined for text-to-speech speaker list.');
  }
  const bucket = process.env.ASSET_BUCKET;
  const key = constructTTSAdminPath('speakers.json');
  const location = {
    bucket: bucket,
    key: key,
    region: process.env.AWS_REGION,
  };
  return location;
}

export function constructTTSOutputPath(...pathComponents: string[]): string {
  return path.join(TTS_OUTPUT_PATH, ...pathComponents);
}

export function constructTTSAdminPath(...pathComponents: string[]): string {
  return path.join(TTS_ADMIN_PATH, ...pathComponents);
}

export function constructTTSPreviewPath(...pathComponents: string[]): string {
  return constructTTSAdminPath('preview', ...pathComponents);
}
